import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./EventsPage.css";
import {
  FALLBACK_CATEGORIES, MONTHS_ORDER, MONTH_NAMES, formatDate,
  COUNTRY_TO_VISA_ID, FALLBACK_IMAGE, SCROLL_OFFSET,
  DEFAULT_ALL_EVENTS, DEFAULT_ALL_MONTHS,
  normalizeEvents, getAvailableYears,
} from "./Eventsconstants.js";

const CONTENT_API = process.env.REACT_APP_TVC_API_URL;

function EventCard({ event, index }) {
  const navigate = useNavigate();
  const [imgError, setImgError] = useState(false);
  const fallback = FALLBACK_IMAGE;

  const handleClick = () => {
    const visaId = COUNTRY_TO_VISA_ID[event.country];
    if (visaId) {
      navigate(`/visa/${visaId}`);
    }
  };

  return (
    <article 
      className="ev-card" 
      style={{ animationDelay: `${Math.min(index * 50, 200)}ms` }} 
      data-aos="fade-up" 
      data-aos-delay={index * 50}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClick(); }}
    >
      <div className="ev-card-img-wrap">
        <img
          className="ev-card-img"
          src={imgError ? fallback : event.image}
          alt={event.title}
          loading="lazy"
          onError={() => setImgError(true)}
        />
        <div className="ev-card-overlay" />
      </div>
      <div className="ev-card-badges">
        <span className="ev-card-tag">{event.tag}</span>
      </div>
      <div className="ev-card-body">
        <div className="ev-card-date-row">
          <span className="ev-card-date">{formatDate(event.date)}</span>
          <span className="ev-card-date-sep" />
          <span className="ev-card-location">{event.city}</span>
        </div>
        <h3 className="ev-card-title">{event.title}</h3>
        <div className="ev-card-footer">
          <div className="ev-card-country">{event.country}</div>
          <div className={`ev-card-visa-label ${event.visaRequired ? "ev-required" : "ev-not-required"}`}>
            <span className="ev-visa-label-dot" aria-hidden="true" />
            {event.visaRequired ? "Visa Required" : "Visa Free"}
          </div>
        </div>
      </div>
    </article>
  );
}

function YearTabs({ years, activeYear, onYearChange }) {
  if (!years.length) return null;
  return (
    <div className="ev-year-tabs" data-aos="fade-up" data-aos-delay="50">
      <div className="ev-year-tabs-inner" data-aos="fade-up" data-aos-delay="100">
        {years.map((year) => (
          <button
            key={year}
            className={`ev-year-tab ${activeYear === year ? "ev-active" : ""}`}
            onClick={() => onYearChange(year)}
          >
            {year}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ─── Category Dropdown — menu opens LEFT (default) ──────── */
function CategoryDropdown({ options, value, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const activeOption = options.find((o) => o.id === value);
  const displayText = activeOption?.label || DEFAULT_ALL_EVENTS;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="ev-dropdown ev-filter-dropdown" ref={dropdownRef}>
      <button
        className={`ev-dropdown-trigger ${isOpen ? "ev-open" : ""}`}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span>{displayText}</span>
        <svg className="ev-dropdown-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {isOpen && (
        /* No extra class — menu aligns left by default */
        <div className="ev-dropdown-menu" role="listbox">
          {options.map((opt) => (
            <button
              key={opt.id}
              className={`ev-dropdown-item ${value === opt.id ? "ev-active" : ""}`}
              role="option"
              aria-selected={value === opt.id}
              onClick={() => {
                onChange(opt.id);
                setIsOpen(false);
              }}
            >
              <span>{opt.label}</span>
              {value === opt.id && (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Country Dropdown — menu opens RIGHT ────────────────── */
function CountryDropdown({ options, value, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const displayText = value === "all" ? "All Countries" : value;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="ev-dropdown ev-country-dropdown" ref={dropdownRef}>
      <button
        className={`ev-dropdown-trigger ${isOpen ? "ev-open" : ""}`}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span>{displayText}</span>
        <svg className="ev-dropdown-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {isOpen && (
        <div className="ev-dropdown-menu" role="listbox">
          <button
            className={`ev-dropdown-item ${value === "all" ? "ev-active" : ""}`}
            role="option"
            aria-selected={value === "all"}
            onClick={() => { onChange("all"); setIsOpen(false); }}
          >
            <span>All Countries</span>
            {value === "all" && (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            )}
          </button>
          {options.map((country) => (
            <button
              key={country}
              className={`ev-dropdown-item ${value === country ? "ev-active" : ""}`}
              role="option"
              aria-selected={value === country}
              onClick={() => { onChange(country); setIsOpen(false); }}
            >
              <span>{country}</span>
              {value === country && (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Month Dropdown — menu opens RIGHT ──────────────────── */
function MonthDropdown({ options, value, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const activeOption = options.find((o) => o.id === value);
  const displayText = activeOption?.label || DEFAULT_ALL_MONTHS;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="ev-dropdown ev-month-dropdown" ref={dropdownRef}>
      <button
        className={`ev-dropdown-trigger ${isOpen ? "ev-open" : ""}`}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span>{displayText}</span>
        <svg className="ev-dropdown-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {isOpen && (
        /* ev-menu-right makes this menu align to the right edge of the trigger */
        <div className="ev-dropdown-menu ev-menu-right" role="listbox">
          {options.map((opt) => (
            <button
              key={opt.key ?? opt.id ?? "all"}
              className={`ev-dropdown-item ${value === opt.id ? "ev-active" : ""}`}
              role="option"
              aria-selected={value === opt.id}
              onClick={() => {
                onChange(opt.id);
                setIsOpen(false);
              }}
            >
              <span>{opt.label}</span>
              {value === opt.id && (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function isPastMonth(month, year) {
  const now = new Date();
  const curYear = now.getFullYear();
  const curMonth = now.getMonth();
  const months = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"];
  const idx = months.indexOf(month);
  return year < curYear || (year === curYear && idx < curMonth);
}

function MonthNavDesktop({ activeMonth, activeYear, visibleMonths, onMonthClick }) {
  if (visibleMonths.length === 0) return null;
  return (
    <div className="ev-month-nav" data-aos="fade-up" data-aos-delay="250">
      <div className="ev-month-nav-inner">
        <button
          className={`ev-month-nav-btn ${activeMonth === null ? "ev-active" : ""}`}
          onClick={() => onMonthClick(null)}
        >
          <span className="ev-month-nav-label">All</span>
          <span className="ev-month-nav-year">View</span>
        </button>
        {visibleMonths.map((m) => {
          const passed = isPastMonth(m.month, m.year);
          return (
            <button
              key={`${m.month}-${m.year}`}
              className={`ev-month-nav-btn ${activeMonth === m.month ? "ev-active" : ""} ${isPastMonth(m.month, m.year) ? "ev-passed" : ""}`}
              onClick={() => onMonthClick(m.month)}
            >
              <span className="ev-month-nav-label">{m.month}</span>
              <span className="ev-month-nav-year">{m.year}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function EventsPage() {
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState(FALLBACK_CATEGORIES);
  const [heroTitle, setHeroTitle] = useState("The world's biggest moments, curated.");
  const [heroSubtitle, setHeroSubtitle] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [activeYear, setActiveYear] = useState(new Date().getFullYear());
  const [activeMonth, setActiveMonth] = useState(null);
  const [activeCountry, setActiveCountry] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [showPast, setShowPast] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const contentRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    fetch(`${CONTENT_API}/content/event`)
      .then((res) => res.json())
      .then((data) => {
        if (data && data.section && data.section.content) {
          const c = data.section.content;
          if (c.heroTitle) setHeroTitle(c.heroTitle);
          if (c.heroSubtitle) setHeroSubtitle(c.heroSubtitle);
          if (Array.isArray(c.categories)) setCategories(c.categories);
          if (Array.isArray(c.featuredEvents)) {
            const normalized = normalizeEvents(c.featuredEvents);
            if (normalized) setEvents(normalized);
          }
        }
      })
      .catch(() => {});
  }, []);

  const availableYears = getAvailableYears(events);
  const availableCountries = [...new Set(events.map(e => e.country).filter(Boolean))].sort();

  // Filter events based on search query
  const searchedEvents = events.filter(e => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (e.title || "").toLowerCase().includes(q)
      || (e.country || "").toLowerCase().includes(q)
      || (e.city || "").toLowerCase().includes(q)
      || (e.category || "").toLowerCase().includes(q)
      || (e.tag || "").toLowerCase().includes(q);
  });

  useEffect(() => {
    if (!availableYears.length) return;
    if (!availableYears.includes(activeYear)) {
      setActiveYear(availableYears[0]);
    }
  }, [availableYears, activeYear]);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 1200);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const scrollToContent = () => {
    if (contentRef.current) {
      const offset = SCROLL_OFFSET;
      const elementPosition = contentRef.current.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: elementPosition - offset, behavior: "smooth" });
    }
  };

  const getFilteredForMonth = (month, year, cat, country) =>
    searchedEvents.filter((e) => {
      const catMatch = (cat ?? activeCategory) === "all" || e.category === (cat ?? activeCategory);
      const countryMatch = (country ?? activeCountry) === "all" || e.country === (country ?? activeCountry);
      if (!countryMatch) return false;
      if (month === null) return catMatch && e.year === year;
      return catMatch && e.month === month && e.year === year;
    });

  const getAllEventsForYear = () =>
    searchedEvents.filter((e) => {
      const categoryMatch = activeCategory === "all" || e.category === activeCategory;
      const countryMatch = activeCountry === "all" || e.country === activeCountry;
      return categoryMatch && countryMatch && e.year === activeYear && (!isPastMonth(e.month, e.year) || showPast);
    }).sort((a, b) => {
      const monthOrder = MONTHS_ORDER.indexOf(a.month) - MONTHS_ORDER.indexOf(b.month);
      if (monthOrder !== 0) return monthOrder;
      return new Date(a.date) - new Date(b.date);
    });

  const groupedEvents = () => {
    const events = getAllEventsForYear();
    const groups = {};
    events.forEach((e) => {
      if (!groups[e.month]) groups[e.month] = [];
      groups[e.month].push(e);
    });
    return groups;
  };

  const visibleMonths = MONTHS_ORDER
    .filter((month) => {
      if (isPastMonth(month, activeYear) && !showPast) return false;
      return searchedEvents.some((e) => {
        const catMatch = activeCategory === "all" || e.category === activeCategory;
        const countryMatch = activeCountry === "all" || e.country === activeCountry;
        return catMatch && countryMatch && e.month === month && e.year === activeYear;
      });
    })
    .map((month) => ({ month, year: activeYear }));

  const showCards = useCallback((month, cat, country, shouldScroll = true) => {
    setActiveMonth(month);
    if (cat !== undefined) setActiveCategory(cat);
    if (country !== undefined) setActiveCountry(country);
    if (shouldScroll) {
      setTimeout(scrollToContent, 100);
    }
  }, []);

  const handleCategoryChange = useCallback((cat) => {
    showCards(null, cat, undefined);
  }, [showCards]);

  const handleCountryChange = useCallback((country) => {
    showCards(null, undefined, country);
  }, [showCards]);

  const handleMonthClick = useCallback((month) => {
    showCards(month);
  }, [showCards]);

  useEffect(() => {
    if (visibleMonths.length > 0) {
      const current = visibleMonths.find((m) => m.month === activeMonth);
      if (!current) showCards(null, undefined, undefined, false);
    }
  }, [activeYear, activeCategory, activeCountry]);

  const activeCards = activeMonth
    ? getFilteredForMonth(activeMonth, activeYear)
    : [];

  /* Month options for the separate month dropdown */
  const monthOptions = [
    { id: null, label: "All Months", key: "all-months" },
    ...visibleMonths.map((m) => ({
      id: m.month,
      label: `${MONTH_NAMES[m.month]} ${m.year}`,
      key: `${m.month}-${m.year}`,
    })),
  ];

  return (
    <div className="ev-page">
      <section className="ev-hero" aria-labelledby="ev-hero-heading" data-aos="fade-up">
        <div className="ev-hero-content" data-aos="fade-up" data-aos-delay="100">
          <h1 className="ev-hero-title" id="ev-hero-heading" dangerouslySetInnerHTML={{ __html: heroTitle }} />
          {heroSubtitle && <p className="ev-hero-subtitle">{heroSubtitle}</p>}
        </div>
      </section>

      {/* ── Search Row ── */}
      <div className="ev-search-row" data-aos="fade-up" data-aos-delay="50">
        <div className="ev-search-bar-wrap" data-aos="fade-up" data-aos-delay="100">
          <input
            ref={searchInputRef}
            className="ev-search-input"
            type="text"
            placeholder="Search events, by country ..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <div className="ev-search-icon-circle">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
          </div>
        </div>
        <button className="ev-hamburger-btn" onClick={() => setFilterOpen(!filterOpen)} aria-label="Toggle filters">
          <svg width="20" height="18" viewBox="0 0 20 18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="2" y1="3" x2="18" y2="3" />
            <line x1="4" y1="9" x2="16" y2="9" />
            <line x1="6" y1="15" x2="14" y2="15" />
          </svg>
        </button>
      </div>

      {/* ── Country & Year Filter Pills ── */}
      <div className="ev-filter-pills-row" data-aos="fade-up" data-aos-delay="150">
        {availableCountries.length > 0 && (
          <>
            <CountryDropdown
              options={availableCountries}
              value={activeCountry}
              onChange={handleCountryChange}
            />
            <span className="ev-pill-sep" />
          </>
        )}
        {availableYears.length > 1 && availableYears.map((year, i) => (
          <button
            key={year}
            className={`ev-pill-btn ${activeYear === year ? "ev-pill-active" : ""}`}
            onClick={() => { setActiveYear(year); setActiveMonth(null); }}
            data-aos="fade-up"
            data-aos-delay={200 + i * 30}
          >
            {year}
          </button>
        ))}
      </div>

      {/* ── Past Events pill — visible on desktop & mobile ── */}
      <div className="ev-past-row" data-aos="fade-up" data-aos-delay="200">
        <button
          className={`ev-pill-btn ${showPast ? "ev-pill-active" : ""}`}
          onClick={() => setShowPast(p => !p)}
          aria-pressed={showPast}
        >
          {showPast ? "Hide Past" : "Past Events"}
        </button>
      </div>

      <div className="ev-filter-bar" role="toolbar" aria-label="Filter events" data-aos="fade-up" data-aos-delay="150">
        <div className="ev-filter-bar-inner" data-aos="fade-up" data-aos-delay="200">
          {isMobile ? (
            /* Mobile: two separate dropdowns side by side */
            <div className="ev-mobile-filter-bar" data-aos="fade-up" data-aos-delay="150">
              <CategoryDropdown
                options={categories}
                value={activeCategory}
                onChange={handleCategoryChange}
              />
              <MonthDropdown
                options={monthOptions}
                value={activeMonth}
                onChange={handleMonthClick}
              />
            </div>
          ) : (
            /* Desktop: pill filter buttons for category */
            categories.map((cat, i) => (
              <button
                key={cat.id}
                className={`ev-filter-btn ${activeCategory === cat.id ? "ev-active" : ""}`}
                onClick={() => handleCategoryChange(cat.id)}
                aria-pressed={activeCategory === cat.id}
                data-aos="fade-up"
                data-aos-delay={200 + i * 50}
              >
                {cat.label}
              </button>
            ))
          )}
        </div>
      </div>

      {/* Desktop month nav (sticky row below filter bar) */}
      {!isMobile && (
        <MonthNavDesktop
          activeMonth={activeMonth}
          activeYear={activeYear}
          visibleMonths={visibleMonths}
          onMonthClick={handleMonthClick}
        />
      )}

      <div className="ev-content" ref={contentRef} data-aos="fade-up" data-aos-delay="100">
        <main className="ev-feed" aria-label="Events list" data-aos="fade-up" data-aos-delay="150">
          {activeMonth === null ? (
            Object.keys(groupedEvents()).length === 0 ? (
              <div className="ev-empty-state" data-aos="fade-up" data-aos-delay="50">
                <p className="ev-empty-title">No matching events</p>
                <p className="ev-empty-sub">
                  Try adjusting your category filter to discover events around the world.
                </p>
              </div>
            ) : (
              <div>
                {Object.entries(groupedEvents()).map(([month, events]) => {
                  const passed = isPastMonth(month, activeYear);
                  return (
                    <div key={month} className="ev-month-group" data-aos="fade-up" data-aos-delay="50">
                      <div className={`ev-month-group-header ${isPastMonth(month, activeYear) ? "ev-passed" : ""}`} data-aos="fade-up" data-aos-delay="100">
                        <h2 className="ev-month-group-title">{MONTH_NAMES[month]}</h2>
                        <span className="ev-month-group-year">{activeYear}</span>
                        <span className="ev-month-group-count">
                          {events.length} {events.length === 1 ? "event" : "events"}
                        </span>
                      </div>
                      <div className="ev-grid">
                        {events.map((event, i) => (
                          <EventCard key={event.id} event={event} index={i} />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          ) : (
            <>
              <div
                className={`ev-month-group-header ${isPastMonth(activeMonth, activeYear) ? "ev-passed" : ""}`}
                style={{ marginBottom: "clamp(18px, 2.5vw, 28px)" }}
                data-aos="fade-up"
                data-aos-delay="50"
              >
                <h2 className="ev-month-group-title">{MONTH_NAMES[activeMonth]}</h2>
                <span className="ev-month-group-year">{activeYear}</span>
                <span className="ev-month-group-count">
                  {activeCards.length} {activeCards.length === 1 ? "event" : "events"}
                </span>
              </div>

              {activeCards.length === 0 ? (
                <div className="ev-empty-state" data-aos="fade-up" data-aos-delay="50">
                  <p className="ev-empty-title">No matching events</p>
                  <p className="ev-empty-sub">
                    Try adjusting your category filter to discover events around the world.
                  </p>
                </div>
              ) : (
                <div className="ev-grid">
                  {activeCards.map((event, i) => (
                    <EventCard key={event.id} event={event} index={i} />
                  ))}
                </div>
              )}
            </>
          )}
        </main>
      </div>

      {/* ── Filter Drawer (mobile/tablet) ── */}
      <div className={`ev-filter-drawer-overlay ${filterOpen ? "ev-filter-drawer-open" : ""}`} onClick={() => setFilterOpen(false)} />

      <div className={`ev-filter-drawer ${filterOpen ? "ev-filter-drawer-open" : ""}`}>
        <div className="ev-filter-drawer-handle" />
        <div className="ev-filter-drawer-content">
          <div className="ev-filter-drawer-section">
            <label className="ev-filter-drawer-label">Country</label>
            <CountryDropdown
              options={availableCountries}
              value={activeCountry}
              onChange={(country) => { handleCountryChange(country); }}
            />
          </div>

          <div className="ev-filter-drawer-section">
            <label className="ev-filter-drawer-label">Year</label>
            <div className="ev-filter-drawer-pills">
              {availableYears.map((year) => (
                <button
                  key={year}
                  className={`ev-pill-btn ${activeYear === year ? "ev-pill-active" : ""}`}
                  onClick={() => { setActiveYear(year); setActiveMonth(null); }}
                >
                  {year} 
                </button>
              ))}
            </div>
          </div>

          <div className="ev-filter-drawer-section">
            <label className="ev-filter-drawer-label">Category</label>
            <CategoryDropdown
              options={categories}
              value={activeCategory}
              onChange={handleCategoryChange}
            />
          </div>

          <div className="ev-filter-drawer-section">
            <label className="ev-filter-drawer-label">Month</label>
            <MonthDropdown
              options={monthOptions}
              value={activeMonth}
              onChange={handleMonthClick}
            />
          </div>

          <div className="ev-filter-drawer-section">
            <label className="ev-filter-drawer-label">Past Events</label>
            <button
              className={`ev-pill-btn ${showPast ? "ev-pill-active" : ""}`}
              style={{ width: "100%", justifyContent: "center" }}
              onClick={() => setShowPast(p => !p)}
              aria-pressed={showPast}
            >
              {showPast ? "Hide Past Events" : "Show Past Events"}
            </button>
          </div>
        </div>

        <div className="ev-drawer-actions">
          <button className="ev-drawer-btn ev-drawer-btn-clear" onClick={() => {
            setActiveCategory("all");
            setActiveCountry("all");
            setActiveYear(new Date().getFullYear());
            setActiveMonth(null);
            setShowPast(false);
            setSearchQuery("");
            setFilterOpen(false);
          }}>
            Clear Filters
          </button>
          <button className="ev-drawer-btn ev-drawer-btn-continue" onClick={() => setFilterOpen(false)}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
